import { NormalizedAction } from '@/types/action-types';
import { Button } from '@/components/ui/button';
import { Eye, Undo2 } from 'lucide-react';
import { formatCurrency } from '@/i18n/formatters';

interface BudgetDetailProps {
  action: NormalizedAction;
  onViewDetails?: () => void;
  onUndoBudget?: () => void;
  isModal?: boolean;
}

export function BudgetDetail({
  action,
  onViewDetails,
  onUndoBudget,
  isModal = false
}: BudgetDetailProps) {
  if (!action.budget) {
    return (
      <div className={`p-6 ${isModal ? '' : 'bg-white h-full'}`}>
        <h3 className='text-lg font-semibold mb-2'>Budget Update</h3>
        <p className='text-sm text-muted-foreground mb-6'>
          Budget information could not be found for this action.
        </p>
      </div>
    );
  }
  
  const { before, after } = action.budget;
  
  const percentChange = before > 0 
    ? Math.round(((after - before) / before) * 100) 
    : 0;
    
  const changeText = percentChange > 0 
    ? `increased by ${percentChange}%` 
    : percentChange < 0 
      ? `decreased by ${Math.abs(percentChange)}%` 
      : 'unchanged';
  
  return (
    <div className={`p-6 ${isModal ? '' : 'bg-white h-full'}`}>
      <h3 className='text-lg font-semibold mb-2'>
        {action.isUndone ? 'Reverted: ' : ''}Budget Update
      </h3>
      <p className='text-sm text-muted-foreground mb-6'>
        Campaign budget was {changeText}.
      </p>
      
      {action.isUndone && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800 font-medium">
            ⚠️ This budget change has been undone
          </p>
        </div>
      )}
      
      {action.reason && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <h4 className="text-sm font-medium text-blue-800 mb-1">Reason</h4>
          <p className="text-sm text-blue-700">{action.reason}</p>
        </div>
      )}
      
      <div className='bg-muted p-4 rounded-md'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <h4 className='text-sm font-medium mb-1'>Previous Budget</h4>
            <p className='text-xl font-bold'>{formatCurrency(before)}</p>
          </div>
          
          <div>
            <h4 className='text-sm font-medium mb-1'>New Budget</h4>
            <p className='text-xl font-bold'>{formatCurrency(after)}</p>
          </div>
        </div>
        
        {!isModal && (
          <div className='mt-4 flex justify-end gap-2'>
            {onUndoBudget && !action.isUndone && (
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1"
                onClick={onUndoBudget}
              >
                <Undo2 className="h-4 w-4" />
                <span>Undo</span>
              </Button>
            )}
            {onViewDetails && (
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1"
                onClick={onViewDetails}
              >
                <Eye className="h-4 w-4" />
                <span>View Details</span>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}